
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { Button, Card, CardBody, Col, Container, Row, Modal, ModalBody } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { MaterialReactTable } from "material-react-table";
import BreadCrumb from "../Common/BreadCrumb";
import { Loader } from "../Utils/Loader";
import { userRoleManagementColoumns } from "./Constant/UserRoleManagementColumns";
import { MenuItem } from "@mui/material";
import { UserRoleCustomButtons } from "./UserRoleCustomButton";
import { useColumnSettings } from "../Utils/useColumnSettings";
import { useSelector } from "react-redux";
import { hasPermission } from "../Utils/UtilityFunctions";
import { useDispatch } from "react-redux";
import { fetchAllUserRoles } from "../../slices/UserRoleManagement/thunk";
import CommonModal from "../Utils/CommonModal";
import { getAllUser } from "../../slices/UserManagement/thunk";
import { deleteUserRole } from "../../slices/UserRoleManagement/DeleteRole/thunk";
import { userRoleMap, DefaultRole, CustomRole } from "./Constant/constants";

export default function UserRoleManagementListing() {
    const [loading, setLoading] = useState()
    const [apiRefetching, setApiRefetching] = useState(false)
    const [messageForPopUp, setMessageForPopUp] = useState("")
    const [modal, setModal] = useState(false)
    const [roleName, setRoleName] = useState("")
    const columns = userRoleManagementColoumns()
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation()
    const operatorId = localStorage.getItem("operatorId") || null;

    const toggleModal = (id) => {
        setRoleName(id)
        setModal(!modal);
        document.body.style.overflow = 'auto'
    };

    const rawPermissions = useSelector(
        (state) => state.permissions.permissionsMap
    );
    const userRoles = useSelector((state) => state.GetAllUserRoles.getAllUserRoles)
    const users = useSelector((state) => state?.GetAllUser?.getAllUser)
    const permissionsMap = new Map(
        rawPermissions.map(([key, value]) => [key, new Set(value)])
    );

    const { domain, modules } = useSelector((state) => state.permissions.permissions)
    const totalModules = modules.filter(module => module?.module.toLowerCase() != "miscellaneous").length

    const rolesPermission = hasPermission(permissionsMap, "administration", "roles")
    const viewPermission = hasPermission(permissionsMap, "miscellaneous", "view")


    const {
        settingsModalOpen,
        viewColumns,
        toggleSettingsModal,
        SettingsSidebar,
        CustomViewModal
    } = useColumnSettings('userRoles', columns);

    const containsNull = viewColumns.some(value => value === undefined);
    const result = !containsNull;
    const data = userRoles?.length > 0 ? userRoles?.map((role) => {
        return {
            rolename: role?.roletype?.toLowerCase() === DefaultRole ? userRoleMap.get(role.rolename) ? userRoleMap.get(role.rolename) : role.rolename : role.rolename,
            roletype: role.roletype,
            modules: `${role?.modules.filter(module => module?.module.toLowerCase() != "miscellaneous").length}/${totalModules} Modules`,  // Here we have to remove the miscellaneous module from the count
            numberOfUsers: users.filter(u => u.role === role?.rolename).length
        }
    }) : []
    const userRolesSummary = {
        numberOfRoles: data.length,
        defaultRoles: data.filter(d => (d.roletype || "").toLowerCase() === DefaultRole).length,
        customRoles: data.filter(d => (d.roletype || "").toLowerCase() === CustomRole).length,
    };


    useEffect(() => {
        if (domain) {
            dispatch(fetchAllUserRoles(setLoading, domain));
        }
    }, [dispatch, domain]);

    useEffect(() => {
        if (location.state?.successMessage) {
            toast.success(location.state.successMessage);
        }
    }, [location.state]);

    useEffect(() => {
        if (apiRefetching) {
            setApiRefetching(false)
        }
    }, [apiRefetching])

    useEffect(() => {
        dispatch(getAllUser(setLoading, operatorId))
    }, [dispatch])

    return (<React.Fragment>
        <div id='csms' className='page-content bg-white'>
            <Container fluid>
                <BreadCrumb title={"User Roles"} />
                <ToastContainer position='top-center' />
                <Row>
                    <Col lg={12}>
                        <CardBody>
                            <Row className="mb-3 mt-1 table-responsive">
                                {loading && (<Loader />)}
                                <MaterialReactTable
                                    icons={{ DragHandleIcon: () => <i className="ri-drag-move-fill" />, }}
                                    displayColumnDefOptions={{
                                        'mrt-row-actions': {
                                            header: '',
                                            enableHiding: true,
                                            size: 53,
                                            grow: true
                                        },
                                    }}
                                    muiTableContainerProps={{
                                        sx: {
                                            border: '1px solid #e9ebec',
                                        }
                                    }}
                                    defaultColumn={{
                                        minSize: 10,
                                        maxSize: 9001,
                                        muiTableBodyCellProps: {
                                            sx: {
                                                fontFamily: "Inter,sans-serif"
                                            }
                                        },
                                        muiTableHeadCellProps: {
                                            sx: {
                                                fontFamily: "Inter,sans-serif"
                                            }
                                        }
                                    }}
                                    columns={result ? columns.filter((column) => column.accessorKey ? viewColumns?.includes(column.accessorKey) : viewColumns?.includes(column.header)) : columns.filter((column) => viewColumns?.includes(column.accessorKey))}
                                    data={data}
                                    enableColumnActions={true}
                                    enableDensityToggle={false}
                                    enableRowActions
                                    renderTopToolbarCustomActions={() => (
                                        <Box sx={{ p: 0, m: 0, width: "100%" }}>
                                            <UserRoleCustomButtons
                                                toggleSettingsModal={toggleSettingsModal}
                                                Roles={data}
                                                navigate={navigate}
                                                UserRolesSummary={userRolesSummary}
                                                rolesPermission={rolesPermission}
                                                viewPermission={viewPermission}
                                            />
                                        </Box>
                                    )}
                                    muiTopToolbarProps={{
                                        sx: {
                                            padding: 0,
                                            minHeight: 'unset',
                                            '& > *': { padding: 0, margin: 0 },
                                        },
                                    }}
                                    autoResetPageIndex={apiRefetching}
                                    renderRowActionMenuItems={({ closeMenu, row }) => [
                                        rolesPermission && rolesPermission.has("view") ? (
                                            <MenuItem className='row-options pb-3' key={`${row.index}.1`} onClick={() => {
                                                const roleType = row?.original?.roletype?.toLowerCase();
                                                const roleName = row?.original?.rolename;
                                                const key =
                                                    roleType === DefaultRole
                                                        ? [...userRoleMap.entries()].find(([k, v]) => v === roleName)?.[0]
                                                        : roleName;

                                                const selectedRow = userRoles?.find(
                                                    (role) => role.rolename?.toLowerCase() === key?.toLowerCase()
                                                );
                                                navigate(`/view-userrole/${selectedRow?.rolename}`, {
                                                    state: { userRole: selectedRow, totalModules: totalModules },
                                                });
                                                closeMenu();
                                            }}><i className="ri-eye-line me-2" /> View</MenuItem>) : null,

                                        rolesPermission && rolesPermission.has("update") && row?.original?.roletype?.toLowerCase() !== DefaultRole ? (
                                            <MenuItem className='row-options pb-3' key={`${row.index}.2`} onClick={() => {
                                                const roleType = row?.original?.roletype?.toLowerCase();
                                                const roleName = row?.original?.rolename;
                                                const key =
                                                    roleType === DefaultRole
                                                        ? [...userRoleMap.entries()].find(([k, v]) => v === roleName)?.[0]
                                                        : roleName;
                                                const selectedRow = userRoles?.find(
                                                    (role) => role.rolename?.toLowerCase() === key?.toLowerCase()
                                                );
                                                navigate(`/edit-userrole/${selectedRow?.rolename}`, {
                                                    state: { userRole: selectedRow },
                                                });

                                                closeMenu();
                                            }}><i className="ri-edit-box-line me-2" /> Edit</MenuItem>) : null,

                                        rolesPermission && rolesPermission.has("delete") && row?.original?.roletype?.toLowerCase() !== DefaultRole ? (
                                            <MenuItem className='row-options pb-3'
                                                key="delete"
                                                onClick={() => { setMessageForPopUp(`Are you sure you want to delete "${row.original.rolename}"?`), toggleModal(row), closeMenu(), setRoleName(row.original.rolename) }}
                                            >
                                                <i className="ri ri-delete-bin-5-line me-2" /> Delete
                                            </MenuItem>) : null,
                                    ]}
                                />

                            </Row>

                        </CardBody>
                    </Col>
                    {CustomViewModal()}
                </Row>
            </Container>
        </div>
        <div>
            <CommonModal modalHeader="Delete Role" open={modal} toggle={toggleModal} buttonText={"Yes, Delete It"} closeButtonText="Close" messageForPopUp={messageForPopUp} modalAction={() => {
                dispatch(deleteUserRole(roleName, setLoading, domain, setApiRefetching))
                toggleModal()
            }} />

        </div>

    </React.Fragment>)
}